import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer,
  glowVariant,
  viewportOnce
} from "@/lib/motion";

export interface ClosingCTAContent {
  headline: string;
  description: string;
  footnote: string;
  primaryCta: string;
  primaryCtaLink: string;
  secondaryCta: string;
  secondaryCtaLink: string;
}

export const closingCTADefaults: ClosingCTAContent = {
  headline: "Growth isn't a campaign. It's a system.",
  description: "This is a serious conversation about growth infrastructure. Not a sales pitch. If that sounds right, let's talk.",
  footnote: "Engagements are senior-led. Start narrow. Expand only when it makes sense.",
  primaryCta: "Start a serious conversation",
  primaryCtaLink: "/contact",
  secondaryCta: "Learn about our approach →",
  secondaryCtaLink: "/about",
};

interface ClosingCTAProps {
  content?: ClosingCTAContent;
}

const ClosingCTA = ({ content: c = closingCTADefaults }: ClosingCTAProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-24 border-t border-border relative overflow-hidden">
      {/* Ultra-light grid texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.012]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(216 30% 50%) 1px, transparent 1px),
            linear-gradient(90deg, hsl(216 30% 50%) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
      
      {/* Gradient mesh */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 140% 100% at 50% 120%, hsl(216 99% 40% / 0.10) 0%, transparent 50%),
            radial-gradient(ellipse 100% 80% at 0% 100%, hsl(169 66% 52% / 0.05) 0%, transparent 40%),
            radial-gradient(ellipse 80% 60% at 100% 80%, hsl(216 80% 55% / 0.04) 0%, transparent 40%)
          `,
        }}
      />
      
      {/* Central glow */}
      <motion.div 
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] pointer-events-none"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={glowVariant}
        style={{
          background: 'radial-gradient(ellipse, hsl(216 99% 60% / 0.07) 0%, transparent 60%)',
          filter: 'blur(70px)',
        }}
      />
      
      {/* Accent glow */}
      <motion.div 
        className="absolute -bottom-24 -right-24 w-[400px] h-[400px] pointer-events-none"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={glowVariant}
        transition={{ delay: 0.2 }}
        style={{
          background: 'radial-gradient(circle, hsl(169 66% 52% / 0.04) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      
      <div className="container-wide relative z-10">
        <motion.div 
          className="max-w-2xl mx-auto text-center"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={sectionVariant}
            className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6"
          >
            {c.headline}
          </motion.h2>
          
          <motion.p 
            variants={sectionVariant}
            className="text-muted-foreground mb-4 leading-relaxed text-lg max-w-xl mx-auto"
          >
            {c.description}
          </motion.p>
          <motion.p
            variants={sectionVariant}
            className="text-xs text-muted-foreground/50 mb-12 max-w-md mx-auto"
          >
            {c.footnote}
          </motion.p>
          
          <motion.div 
            variants={sectionVariant}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button 
              size="lg"
              className="group text-base px-8"
              asChild
            >
              <Link to={c.primaryCtaLink}>
                {c.primaryCta}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Link 
              to={c.secondaryCtaLink}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-3"
            >
              {c.secondaryCta}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClosingCTA;
